import React, { useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { TrendingUp, TrendingDown } from 'lucide-react';

const AssetsSection = ({ language, onRegister }) => {
  const [activeCategory, setActiveCategory] = useState('forex');
  const { tt } = useLanguage();

  const translations = {
    ru: {
      title: 'Торгуйте популярными активами',
      subtitle: 'Более 200 торговых инструментов в вашем распоряжении',
      categories: {
        forex: 'Валютные пары',
        crypto: 'Криптовалюты', 
        stocks: 'Акции',
        commodities: 'Товары'
      },
      viewAll: 'Посмотреть все',
      change: 'Изменение',
      spread: 'Спред'
    },
    en: {
      title: 'Trade Popular Assets',
      subtitle: 'More than 200 trading instruments at your disposal',
      categories: {
        forex: 'Currency Pairs',
        crypto: 'Cryptocurrencies',
        stocks: 'Stocks', 
        commodities: 'Commodities'
      },
      viewAll: 'View All',
      change: 'Change',
      spread: 'Spread'
    },
    kk: {
      title: 'Танымал активтермен сауда жасаңыз',
      subtitle: 'Сіздің қолыңызда 200-ден астам сауда құралдары',
      categories: {
        forex: 'Валюта жұптары',
        crypto: 'Криптовалюталар',
        stocks: 'Акциялар',
        commodities: 'Тауарлар'
      },
      viewAll: 'Барлығын көру',
      change: 'Өзгеріс',
      spread: 'Спред'
    }
  };

  const assets = {
    forex: [
      { symbol: 'EUR/USD', name: 'Euro vs US Dollar', price: '1.0850', change: '+0.12%', isPositive: true, spread: '0.5' },
      { symbol: 'GBP/USD', name: 'British Pound vs US Dollar', price: '1.2650', change: '-0.08%', isPositive: false, spread: '0.7' },
      { symbol: 'USD/JPY', name: 'US Dollar vs Japanese Yen', price: '149.50', change: '+0.25%', isPositive: true, spread: '0.8' },
      { symbol: 'AUD/USD', name: 'Australian Dollar vs US Dollar', price: '0.6720', change: '-0.15%', isPositive: false, spread: '0.9' },
      { symbol: 'USD/CHF', name: 'US Dollar vs Swiss Franc', price: '0.9120', change: '+0.18%', isPositive: true, spread: '0.6' },
      { symbol: 'NZD/USD', name: 'New Zealand Dollar vs US Dollar', price: '0.6150', change: '-0.22%', isPositive: false, spread: '1.1' }
    ],
    crypto: [
      { symbol: 'BTC/USD', name: 'Bitcoin', price: '43,250', change: '+2.89%', isPositive: true, spread: '15' },
      { symbol: 'ETH/USD', name: 'Ethereum', price: '2,650', change: '-1.25%', isPositive: false, spread: '8' },
      { symbol: 'LTC/USD', name: 'Litecoin', price: '68.50', change: '+4.82%', isPositive: true, spread: '0.25' },
      { symbol: 'XRP/USD', name: 'Ripple', price: '0.615', change: '-2.45%', isPositive: false, spread: '0.01' },
      { symbol: 'ADA/USD', name: 'Cardano', price: '0.385', change: '+6.78%', isPositive: true, spread: '0.005' },
      { symbol: 'DOT/USD', name: 'Polkadot', price: '5.25', change: '+8.12%', isPositive: true, spread: '0.05' }
    ],
    stocks: [
      { symbol: 'AAPL', name: 'Apple Inc.', price: '175.30', change: '+1.42%', isPositive: true, spread: '0.02' },
      { symbol: 'GOOGL', name: 'Alphabet Inc.', price: '130.25', change: '-0.68%', isPositive: false, spread: '0.03' },
      { symbol: 'TSLA', name: 'Tesla Inc.', price: '245.80', change: '+4.75%', isPositive: true, spread: '0.05' },
      { symbol: 'MSFT', name: 'Microsoft Corp.', price: '378.90', change: '+0.89%', isPositive: true, spread: '0.02' },
      { symbol: 'AMZN', name: 'Amazon.com Inc.', price: '145.60', change: '-3.12%', isPositive: false, spread: '0.04' },
      { symbol: 'NVDA', name: 'NVIDIA Corp.', price: '875.50', change: '+5.67%', isPositive: true, spread: '0.08' }
    ],
    commodities: [
      { symbol: 'XAU/USD', name: 'Золото', price: '1,985.50', change: '+0.65%', isPositive: true, spread: '0.50' },
      { symbol: 'XAG/USD', name: 'Серебро', price: '23.45', change: '-1.45%', isPositive: false, spread: '0.03' },
      { symbol: 'WTI/USD', name: 'Нефть WTI', price: '75.80', change: '+1.12%', isPositive: true, spread: '0.05' },
      { symbol: 'XPT/USD', name: 'Платина', price: '945.20', change: '+0.56%', isPositive: true, spread: '1.20' }
    ]
  };

  const t = translations[language] || translations['ru'];

  return (
    <div className="bg-gray-50 py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {t.title}
          </h2>
          <p className="text-xl text-gray-600">
            {t.subtitle}
          </p>
        </div>

        {/* Category Tabs */}
        <div className="flex flex-wrap justify-center mb-12">
          {Object.entries(t.categories).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setActiveCategory(key)}
              className={`px-6 py-3 mx-2 mb-2 rounded-lg font-semibold transition-all duration-300 ${
                activeCategory === key
                  ? 'bg-gradient-to-r from-emerald-600 to-green-600 text-white shadow-lg'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Assets Grid */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                    {language === 'ru' ? 'Актив' : 'Asset'}
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                    {language === 'ru' ? 'Название' : 'Name'}
                  </th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-gray-900">
                    {language === 'ru' ? 'Цена' : 'Price'}
                  </th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-gray-900">
                    {t.change}
                  </th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-gray-900">
                    {t.spread}
                  </th>
                  <th className="px-6 py-4"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {assets[activeCategory].map((asset, index) => (
                  <tr key={index} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="font-semibold text-gray-900">
                        {asset.symbol}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-gray-600">
                        {asset.name}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="font-mono font-semibold text-gray-900">
                        ${asset.price}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className={`flex items-center justify-end ${
                        asset.isPositive ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {asset.isPositive ? (
                          <TrendingUp className="w-4 h-4 mr-1" />
                        ) : (
                          <TrendingDown className="w-4 h-4 mr-1" />
                        )}
                        {asset.change}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="text-gray-600">
                        {asset.spread}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button 
                        onClick={onRegister}
                        className="px-4 py-2 bg-gradient-to-r from-emerald-600 to-green-600 text-white rounded-lg text-sm font-medium hover:from-emerald-700 hover:to-green-700 transition-all duration-200">
                        {language === 'ru' ? 'Торговать' : 'Trade'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* View All Assets */}
        <div className="text-center mt-8">
          <button 
            onClick={onRegister}
            className="px-8 py-3 border-2 border-emerald-600 text-emerald-600 rounded-lg font-semibold hover:bg-emerald-600 hover:text-white transition-colors">
            {t.viewAll}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssetsSection;